import { type CSSProperties, useLayoutEffect, useRef } from "react";
import { LogoWordmark } from "../../components/Logo";
import { INTRO_TILES, clamp } from "./rosterJourneyModel";

// The opening's timeline, in ms from the first frame. The twelve cards float,
// glide one after another into tiles of the Tarii mark, the rest of the mark
// ripples in, the wordmark unfurls, the line lands, the lockup holds alone,
// then dissolves towards the viewer into the overview.
export const OPENING = {
  gather: 950,
  flight: 1150,
  stagger: 65,
  panels: 850,
  panelsFor: 650,
  fill: 2350,
  fillRipple: 110,
  fillFor: 460,
  word: 2750,
  wordFor: 800,
  line: 3250,
  lineStep: 85,
  lineFor: 700,
  assembled: 4000,
  exit: 5300,
  exitFor: 850,
} as const;
export const OPENING_END = OPENING.exit + OPENING.exitFor;

export const OPENING_LINE = "The roster that builds itself.";
const LINE_WORDS = OPENING_LINE.split(" ").map((word, index) => ({
  word,
  key: `${index}:${word}`,
}));

// The mark as data (public/tarii-mark-combined.svg): four 122-unit panels on a
// 140-unit pitch in a 264-unit box, each holding a 3×3 grid of 27-unit tiles.
const MARK_BOX = 264;
const TILE = 27;
const TILE_AT = [17, 49, 81] as const;
const PANEL_AT = [
  [0, 0],
  [140, 0],
  [0, 140],
  [140, 140],
] as const;
const PANEL_FILLS = [
  [
    "#315A60",
    "#E9EEEA",
    "#A3B5B6",
    "#315A60",
    "#80999D",
    "#E9EEEA",
    "#E9EEEA",
    "#80999D",
    "#A3B5B6",
  ],
  [
    "#CFE5DD",
    "#9FCDBB",
    "#BBDDCF",
    "#EBF0EB",
    "#EBF0EB",
    "#BBDDCF",
    "#CFE5DD",
    "#9FCDBB",
    "#EBF0EB",
  ],
  [
    "#CFE5DD",
    "#EBF0EB",
    "#EBF0EB",
    "#CFE5DD",
    "#E0ECE5",
    "#BBDDCF",
    "#EBF0EB",
    "#E0ECE5",
    "#BBDDCF",
  ],
  [
    "#E9EEEA",
    "#758185",
    "#A0AAAD",
    "#2D4247",
    "#E9EEEA",
    "#A0AAAD",
    "#2D4247",
    "#758185",
    "#E9EEEA",
  ],
] as const;

type MarkTile = { x: number; y: number; fill: string; ripple: number };
const MARK_TILES: ReadonlyArray<MarkTile> = PANEL_AT.flatMap(([px, py], panel) =>
  (PANEL_FILLS[panel] ?? []).map((fill, k) => {
    const row = Math.floor(k / 3);
    const col = k % 3;
    // Distance from the mark's centre on the 6×6 grid drives the fill ripple.
    const gx = (panel % 2) * 3 + col;
    const gy = Math.floor(panel / 2) * 3 + row;
    return {
      x: (px + (TILE_AT[col] ?? 0)) / MARK_BOX,
      y: (py + (TILE_AT[row] ?? 0)) / MARK_BOX,
      fill,
      ripple: Math.hypot(gx - 2.5, gy - 2.5),
    };
  }),
);

// Which mark tile each opening card becomes (index into MARK_TILES), chosen
// so every card lands in the panel on its own side of the screen.
const CARD_SLOT = [0, 4, 10, 14, 3, 28, 18, 26, 33, 30, 16, 23] as const;
const SLOTS = new Set<number>(CARD_SLOT);

// Cards leave in a clockwise sweep, starting from the top.
const LAUNCH_ORDER = (() => {
  const bearing = (i: number) => {
    const tile = INTRO_TILES[i];
    if (!tile) return 0;
    const angle = Math.atan2(tile.y - 47, tile.x - 50) + Math.PI / 2;
    return (angle + Math.PI * 2) % (Math.PI * 2);
  };
  const order = INTRO_TILES.map((_, i) => i).sort((a, b) => bearing(a) - bearing(b));
  const rank: number[] = [];
  order.forEach((card, position) => {
    rank[card] = position;
  });
  return rank;
})();

const easeInOut = (t: number) => (t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2);
const easeOut = (t: number) => 1 - (1 - t) ** 3;
const easeIn = (t: number) => t ** 3;
const easeOutBack = (t: number) => 1 + 2.2 * (t - 1) ** 3 + 1.2 * (t - 1) ** 2;
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** 0 → 1 as card `i` travels from its float position into its mark tile. */
export const cardProgress = (i: number, t: number) =>
  clamp((t - OPENING.gather - (LAUNCH_ORDER[i] ?? 0) * OPENING.stagger) / OPENING.flight);

type Geometry = {
  box: { w: number; h: number };
  mark: { left: number; top: number; size: number };
  cards: Array<{ w: number; h: number }>;
};

/**
 * The opening, drawn from the clock `t` (ms on the OPENING timeline). It is a
 * pure function of `t`, so fast-forwarding, replaying and screenshots all
 * agree. Card geometry is measured after each commit and used by the next
 * frame; the mark only moves after every card has landed, so the one-frame
 * lag never shows.
 */
export function OpeningAssembly({ t }: { t: number }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const geometry = useRef<Geometry | null>(null);

  useLayoutEffect(() => {
    const box = boxRef.current?.getBoundingClientRect();
    const mark = markRef.current?.getBoundingClientRect();
    if (!box || !mark || !box.width) return;
    const previous = geometry.current?.cards ?? [];
    geometry.current = {
      box: { w: box.width, h: box.height },
      mark: { left: mark.left - box.left, top: mark.top - box.top, size: mark.width },
      // A card's own size is read while it still floats; once it starts to
      // shrink, the size it set off with is kept.
      cards: INTRO_TILES.map((_, i) => {
        const card = cardRefs.current[i];
        const kept = previous[i];
        if (card && cardProgress(i, t) === 0) return { w: card.offsetWidth, h: card.offsetHeight };
        return kept ?? { w: 0, h: 0 };
      }),
    };
  });

  const panels = easeOut(clamp((t - OPENING.panels) / OPENING.panelsFor));
  const word = easeInOut(clamp((t - OPENING.word) / OPENING.wordFor));
  const settle = easeOut(clamp(t / OPENING.assembled));
  const leaving = easeIn(clamp((t - OPENING.exit) / OPENING.exitFor));
  const glow = easeOut(clamp((t - OPENING.fill) / (OPENING.assembled - OPENING.fill)));
  const geo = geometry.current;
  const tileSize = geo ? (geo.mark.size * TILE) / MARK_BOX : 0;

  return (
    <div ref={boxRef} className="rj-opening op-opening">
      <div
        className="op-glow"
        aria-hidden="true"
        style={{
          opacity: glow * (1 - leaving),
          transform: `translate(-50%, -50%) scale(${0.7 + 0.3 * glow})`,
        }}
      />
      <div
        className="op-stage"
        style={
          {
            opacity: 1 - leaving,
            filter: leaving ? `blur(${leaving * 12}px)` : undefined,
            transform: `translate(-50%, -50%) scale(${1.035 - 0.035 * settle + 0.1 * leaving})`,
            "--word": word,
          } as CSSProperties
        }
      >
        <div className="op-lockup">
          <div ref={markRef} className="op-mark">
            {PANEL_AT.map(([x, y]) => (
              <span
                key={`panel-${x}-${y}`}
                className="op-panel"
                style={{
                  left: `${((x + 1) / MARK_BOX) * 100}%`,
                  top: `${((y + 1) / MARK_BOX) * 100}%`,
                  opacity: panels,
                  transform: `scale(${0.9 + 0.1 * panels})`,
                }}
              />
            ))}
            {MARK_TILES.map((tile, index) => {
              const slotCard = SLOTS.has(index) ? CARD_SLOT.indexOf(index as never) : -1;
              let shown: number;
              let scale = 1;
              if (slotCard >= 0) {
                shown = cardProgress(slotCard, t) >= 1 ? 1 : 0;
              } else {
                const q = clamp(
                  (t - OPENING.fill - tile.ripple * OPENING.fillRipple) / OPENING.fillFor,
                );
                shown = easeOut(q);
                scale = 0.35 + 0.65 * easeOutBack(q);
              }
              return (
                <span
                  key={`tile-${tile.x}-${tile.y}`}
                  className="op-tile"
                  style={{
                    left: `${tile.x * 100}%`,
                    top: `${tile.y * 100}%`,
                    background: tile.fill,
                    opacity: shown,
                    transform: scale === 1 ? undefined : `scale(${scale})`,
                  }}
                />
              );
            })}
          </div>
          <div className="op-word">
            <LogoWordmark height="100%" liveDots />
          </div>
        </div>
        <h1 className="op-line" aria-label={OPENING_LINE}>
          {LINE_WORDS.map(({ word: part, key }, index) => {
            const q = easeOut(
              clamp((t - OPENING.line - index * OPENING.lineStep) / OPENING.lineFor),
            );
            return (
              <span
                key={key}
                aria-hidden="true"
                style={{
                  opacity: q,
                  filter: q < 1 ? `blur(${(1 - q) * 8}px)` : undefined,
                  transform: q < 1 ? `translateY(${(1 - q) * 0.45}em)` : undefined,
                }}
              >
                {part}
              </span>
            );
          })}
        </h1>
      </div>
      <div className="rj-orbit-layer" aria-hidden="true">
        {INTRO_TILES.map((tile, i) => {
          const p = cardProgress(i, t);
          if (p >= 1) return null;
          const card = geo?.cards[i];
          const slot = MARK_TILES[CARD_SLOT[i] ?? 0];
          const flying = p > 0 && geo !== null && card !== undefined && card.w > 0 && slot;
          const tilt = ((i % 5) - 2) * 4;
          let place: CSSProperties = {
            left: `${tile.x}%`,
            top: `${tile.y}%`,
            transform: `translate(-50%, -50%) rotate(${tilt}deg)`,
          };
          let flight: CSSProperties = {};
          let fade = 1;
          if (flying) {
            // A gentle clockwise arc (quadratic Bézier) from the float
            // position to the tile's centre, shrinking into the tile.
            const e = easeInOut(p);
            // Cards keep their shape for most of the trip and compress on arrival.
            const shrink = easeInOut(clamp((p - 0.2) / 0.8));
            const from = { x: (tile.x / 100) * geo.box.w, y: (tile.y / 100) * geo.box.h };
            const to = {
              x: geo.mark.left + slot.x * geo.mark.size + tileSize / 2,
              y: geo.mark.top + slot.y * geo.mark.size + tileSize / 2,
            };
            const bend = 0.24;
            const control = {
              x: (from.x + to.x) / 2 - (to.y - from.y) * bend,
              y: (from.y + to.y) / 2 + (to.x - from.x) * bend,
            };
            place = {
              left: (1 - e) ** 2 * from.x + 2 * (1 - e) * e * control.x + e ** 2 * to.x,
              top: (1 - e) ** 2 * from.y + 2 * (1 - e) * e * control.y + e ** 2 * to.y,
              zIndex: 20,
              transform: `translate(-50%, -50%) rotate(${tilt * (1 - e)}deg) rotateY(${Math.sin(Math.PI * p) * -16}deg)`,
            };
            flight = {
              width: lerp(card.w, tileSize, shrink),
              height: lerp(card.h, tileSize, shrink),
              "--land": shrink,
              "--paint": easeInOut(clamp((p - 0.45) / 0.5)),
              "--slot-fill": slot.fill,
              "--orbit-drift": `${22 * (1 - e)}px`,
              "--wobble": 1 - e,
            } as CSSProperties;
            fade = 1 - clamp((p - 0.15) / 0.35);
          }
          return (
            <div key={`${tile.kind}-${tile.label}`} className="rj-orbit-position" style={place}>
              <div className="op-card-enter" style={{ animationDelay: `${i * 55}ms` }}>
                <div
                  ref={(el) => {
                    cardRefs.current[i] = el;
                  }}
                  className="rj-orbit-tile"
                  data-kind={tile.kind}
                  data-landing={flying ? "" : undefined}
                  style={
                    {
                      "--orbit-delay": `${-i * 1.7}s`,
                      "--orbit-duration": `${3.8 + (i % 4) * 0.7}s`,
                      "--orbit-drift": "22px",
                      ...flight,
                    } as CSSProperties
                  }
                >
                  <CardFace kind={tile.kind} label={tile.label} fade={fade} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CardFace({ kind, label, fade = 1 }: { kind: string; label: string; fade?: number }) {
  return (
    <>
      <span style={{ opacity: fade }}>
        {kind === "Member" ? "●" : kind === "Station" ? "□" : "!"}
      </span>
      <div style={{ opacity: fade }}>
        <small>{kind}</small>
        <strong>{label}</strong>
      </div>
    </>
  );
}
